import * as React from "react";
import { cn } from "@/lib/utils";
import { ArrowRight, Quote } from "lucide-react";
import { StatusBadge, type StatusTone } from "./badges";

export const Card = ({
  className,
  interactive,
  tone = "default",
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { interactive?: boolean; tone?: "default" | "mist" }) => (
  <div
    className={cn(
      "ds-card",
      tone === "mist" && "bg-surface",
      interactive && "ds-card-interactive",
      className,
    )}
    {...props}
  />
);

/** Capability card — icon, title, copy, optional status chip. */
export const CapabilityCard = ({
  icon: Icon,
  title,
  children,
  status,
  statusTone = "info",
  className,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  title: string;
  children?: React.ReactNode;
  status?: string;
  statusTone?: StatusTone;
  className?: string;
}) => (
  <Card interactive className={cn("flex h-full flex-col gap-3", className)}>
    {Icon ? (
      <span className="inline-flex size-10 items-center justify-center rounded-lg border border-border bg-surface text-primary">
        <Icon className="size-5" />
      </span>
    ) : null}
    <h3 className="ds-h4">{title}</h3>
    <div className="ds-body text-muted-foreground">{children}</div>
    {status ? (
      <div className="mt-auto pt-2">
        <StatusBadge tone={statusTone}>{status}</StatusBadge>
      </div>
    ) : null}
  </Card>
);

/** Product card — media, name, summary, bullet list, CTA. */
export const ProductCard = ({
  image,
  imageAlt = "",
  name,
  summary,
  bullets = [],
  ctaLabel,
  ctaHref,
  className,
}: {
  image?: string;
  imageAlt?: string;
  name: string;
  summary: string;
  bullets?: string[];
  ctaLabel?: string;
  ctaHref?: string;
  className?: string;
}) => (
  <Card interactive className={cn("flex h-full flex-col gap-4 p-0 overflow-hidden", className)}>
    {image ? (
      <div className="aspect-[16/9] w-full overflow-hidden border-b border-border bg-surface">
        <img src={image} alt={imageAlt} loading="lazy" className="size-full object-cover" />
      </div>
    ) : null}
    <div className="flex flex-1 flex-col gap-3 p-5 sm:p-6">
      <h3 className="ds-h3">{name}</h3>
      <p className="ds-body text-muted-foreground">{summary}</p>
      {bullets.length ? (
        <ul className="mt-1 space-y-2">
          {bullets.map((b) => (
            <li key={b} className="ds-caption flex gap-2">
              <span aria-hidden className="mt-[0.45rem] size-1.5 shrink-0 rounded-full bg-signal" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {ctaLabel && ctaHref ? (
        <a href={ctaHref} className="ds-nav-link mt-auto inline-flex items-center gap-1.5 pt-3 text-primary">
          {ctaLabel} <ArrowRight className="size-4" />
        </a>
      ) : null}
    </div>
  </Card>
);

/** Statistic card — value, label, optional source note. */
export const StatCard = ({
  value,
  label,
  note,
  className,
}: {
  value: string;
  label: string;
  note?: string;
  className?: string;
}) => (
  <Card className={cn("flex h-full flex-col gap-1", className)}>
    <span className="ds-stat text-primary">{value}</span>
    <span className="ds-h5 font-semibold">{label}</span>
    {note ? <span className="ds-legal mt-1">{note}</span> : null}
  </Card>
);

export const TestimonialCard = ({
  quote,
  author,
  role,
  className,
}: {
  quote: string;
  author: string;
  role?: string;
  className?: string;
}) => (
  <Card className={cn("flex h-full flex-col gap-4", className)}>
    <Quote aria-hidden className="size-6 text-signal" />
    <blockquote className="ds-body text-foreground">{quote}</blockquote>
    <footer className="mt-auto">
      <p className="ds-h5">{author}</p>
      {role ? <p className="ds-caption">{role}</p> : null}
    </footer>
  </Card>
);

/** Evidence / citation block — claim plus numbered source list. */
export const EvidenceBlock = ({
  claim,
  citations,
  className,
}: {
  claim: React.ReactNode;
  citations: { label: string; href?: string }[];
  className?: string;
}) => (
  <figure className={cn("rounded-xl border-l-2 border-l-signal border border-border bg-surface p-5", className)}>
    <blockquote className="ds-body">{claim}</blockquote>
    <figcaption className="mt-4">
      <p className="ds-label mb-2">Sources</p>
      <ol className="space-y-1">
        {citations.map((c, i) => (
          <li key={c.label} className="ds-legal">
            <span className="tabular-nums">{i + 1}. </span>
            {c.href ? (
              <a href={c.href} target="_blank" rel="noopener noreferrer" className="ds-link">
                {c.label}
              </a>
            ) : (
              c.label
            )}
          </li>
        ))}
      </ol>
    </figcaption>
  </figure>
);

export const ResourceCard = ({
  kind,
  title,
  description,
  meta,
  href,
  className,
}: {
  kind: string;
  title: string;
  description?: string;
  meta?: string;
  href: string;
  className?: string;
}) => (
  <a
    href={href}
    className={cn(
      "ds-card ds-card-interactive group flex h-full flex-col gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className,
    )}
  >
    <span className="ds-label text-signal">{kind}</span>
    <span className="ds-h4 group-hover:text-primary">{title}</span>
    {description ? <span className="ds-caption">{description}</span> : null}
    {meta ? <span className="ds-legal mt-auto pt-3">{meta}</span> : null}
  </a>
);
